import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";

import type {
  KitchenArrivalRow,
  ShotNumberOverlay,
  Vec3,
} from "./RallyBoardPopup";
import { ActiveRallyVideo } from "./ActiveRallyVideo";
import type { FirefightClip } from "./GameStory_firefights";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";

export type AtpDetectionMethod = "pbVisionFlag" | "ballPath";

export type AtpEvent = {
  rallyIndex: number;
  shotIndex: number;
  shotNumber: number;
  team: 0 | 1;
  playerId: number;
  methods: AtpDetectionMethod[];
  notes: string[];
  shotStartMs: number;
  shotEndMs: number;
  shotType?: string | null;
  strokeType?: string | null;
  startX?: number | null;
  endX?: number | null;
  peakZ?: number | null;
  netCrossingX?: number | null;
  netCrossingZ?: number | null;
  heightOverNet?: number | null;
  netClearanceAtCrossing?: number | null;
  outsideByFt?: number | null;
};

export type AtpClip = {
  key: string;
  event: AtpEvent;
  clipStartMs: number;
  clipEndMs: number;
};

type ScopeMode = "allRallies" | "thisRally";
type MethodFilter = "all" | AtpDetectionMethod;
const PICKLEBALL_NET_Y_FT = 22;
const PICKLEBALL_NET_HEIGHT_FT = 3.0;
const ATP_MIN_OUTSIDE_AT_NET_FT = 0.5;
const ATP_CLIP_EXTRA_BEFORE_PREV_SHOT_MS = 650;

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function lower(v: unknown): string {
  return String(v ?? "").toLowerCase();
}

function teamFromPlayerId(playerId: number): 0 | 1 {
  return playerId === 0 || playerId === 1 ? 0 : 1;
}

function pointFrom(v: unknown): Vec3 | null {
  const p = v as Partial<Vec3> | null | undefined;
  const x = num(p?.x);
  const y = num(p?.y);
  const z = num(p?.z);
  if (x == null || y == null) return null;
  return { x, y, z: z ?? 0 };
}

function trajectoryPoints(overlay: ShotNumberOverlay): {
  start: Vec3 | null;
  peak: Vec3 | null;
  end: Vec3 | null;
} {
  const rawTrajectory = overlay.rawShot?.resulting_ball_movement?.trajectory;
  return {
    start: pointFrom(
      overlay.trajectoryStart?.location ?? rawTrajectory?.start?.location,
    ),
    peak: pointFrom(overlay.trajectoryPeak ?? rawTrajectory?.peak),
    end: pointFrom(
      overlay.trajectoryEnd?.location ?? rawTrajectory?.end?.location,
    ),
  };
}

function outsideSidelineByFt(x: number): number {
  if (x < 0) return -x;
  if (x > 20) return x - 20;
  return 0;
}

function inCourtX(x: number | null | undefined): boolean {
  return typeof x === "number" && x >= -0.2 && x <= 20.2;
}

function interpolateAtY(
  a: Vec3 | null,
  b: Vec3 | null,
  y: number,
): Vec3 | null {
  if (!a || !b) return null;
  const dy = b.y - a.y;
  if (Math.abs(dy) < 0.001) return null;
  const t = (y - a.y) / dy;
  if (t < 0 || t > 1) return null;
  return {
    x: a.x + (b.x - a.x) * t,
    y,
    z: a.z + (b.z - a.z) * t,
  };
}

function estimateNetPlaneCrossing(points: {
  start: Vec3 | null;
  peak: Vec3 | null;
  end: Vec3 | null;
}): Vec3 | null {
  return (
    interpolateAtY(points.start, points.peak, PICKLEBALL_NET_Y_FT) ??
    interpolateAtY(points.peak, points.end, PICKLEBALL_NET_Y_FT) ??
    interpolateAtY(points.start, points.end, PICKLEBALL_NET_Y_FT)
  );
}

function hasPbVisionAtpFlag(overlay: ShotNumberOverlay): boolean {
  const raw = overlay.rawShot as any;
  if (!raw) return false;

  const directValues = [
    raw.atp,
    raw.is_atp,
    raw.isATP,
    raw.around_the_post,
    raw.is_around_the_post,
    raw.tags,
    raw.flags,
    raw.badges,
    raw.shot_tags,
    raw.classifications,
    raw.shot_type,
    raw.stroke_type,
    overlay.shotType,
    overlay.label,
    overlay.featuresLabel,
  ];

  const text = directValues
    .map((value) => {
      if (typeof value === "boolean") return value ? " atp " : "";
      if (Array.isArray(value)) return value.join(" ");
      if (value && typeof value === "object") return JSON.stringify(value);
      return String(value ?? "");
    })
    .join(" ")
    .toLowerCase();

  return (
    /\batp\b/.test(text) ||
    text.includes("around the post") ||
    text.includes("around-the-post")
  );
}

function explicitAtpShotType(overlay: ShotNumberOverlay): boolean {
  return (
    lower((overlay.rawShot as any)?.shot_type) === "atp" ||
    lower(overlay.shotType) === "atp"
  );
}

function detectAtpByBallPath(overlay: ShotNumberOverlay): {
  detected: boolean;
  notes: string[];
  outsideByFt: number | null;
  startX: number | null;
  endX: number | null;
  peakZ: number | null;
  netCrossingX: number | null;
  netCrossingZ: number | null;
  heightOverNet: number | null;
  netClearanceAtCrossing: number | null;
  invalidAboveNetCrossing: boolean;
} {
  const rawMovement = (overlay.rawShot as any)?.resulting_ball_movement;
  const points = trajectoryPoints(overlay);
  const { start, peak, end } = points;
  const netCrossing = estimateNetPlaneCrossing(points);
  const xs = [start?.x, peak?.x, end?.x].filter(
    (x): x is number => typeof x === "number" && Number.isFinite(x),
  );
  const outsideByFt = xs.length
    ? Math.max(...xs.map(outsideSidelineByFt))
    : null;
  const startOutside = start?.x != null ? outsideSidelineByFt(start.x) : 0;
  const endOutside = end?.x != null ? outsideSidelineByFt(end.x) : 0;
  const peakOutside = peak?.x != null ? outsideSidelineByFt(peak.x) : 0;
  const peakZ = peak?.z ?? null;
  const netCrossingOutside =
    netCrossing?.x != null ? outsideSidelineByFt(netCrossing.x) : null;
  const heightOverNet = num(rawMovement?.height_over_net);
  const crossedNet = rawMovement?.crossed_net === true;
  const netClearanceAtCrossing =
    heightOverNet != null
      ? heightOverNet
      : netCrossing?.z != null
        ? netCrossing.z - PICKLEBALL_NET_HEIGHT_FT
        : null;
  const crossesAboveNet =
    netClearanceAtCrossing != null && netClearanceAtCrossing > 0;
  const crossesLikeAtp =
    netCrossing != null &&
    (netCrossingOutside ?? 0) >= ATP_MIN_OUTSIDE_AT_NET_FT &&
    netClearanceAtCrossing != null &&
    netClearanceAtCrossing <= 0;

  const endZone = lower(
    overlay.endZone ??
      overlay.rawShot?.resulting_ball_movement?.trajectory?.end?.zone,
  );
  const landedInCourt =
    inCourtX(end?.x) &&
    !endZone.includes("out") &&
    !endZone.includes("net") &&
    !endZone.includes("fault");

  const wideEnough = Math.max(startOutside, peakOutside, endOutside) >= 1.0;
  const notServe = overlay.shotNumber > 1;

  const notes: string[] = [];
  if (wideEnough) {
    notes.push(`path outside sideline by ${outsideByFt?.toFixed(1)} ft`);
  }
  if (landedInCourt) notes.push("lands back in court");
  if (netCrossing) {
    notes.push(
      `net-plane path x=${netCrossing.x.toFixed(1)}, z=${netCrossing.z.toFixed(1)}`,
    );
  }
  if (heightOverNet != null) {
    notes.push(`PBV net clearance ${heightOverNet.toFixed(1)} ft`);
  } else if (netClearanceAtCrossing != null) {
    notes.push(`estimated net clearance ${netClearanceAtCrossing.toFixed(1)} ft`);
  }
  if (crossesLikeAtp) notes.push("crosses the net line at or below net height");

  return {
    detected:
      notServe &&
      wideEnough &&
      landedInCourt &&
      crossesLikeAtp,
    notes,
    outsideByFt,
    startX: start?.x ?? null,
    endX: end?.x ?? null,
    peakZ,
    netCrossingX: netCrossing?.x ?? null,
    netCrossingZ: netCrossing?.z ?? null,
    heightOverNet,
    netClearanceAtCrossing,
    invalidAboveNetCrossing: crossedNet && crossesAboveNet,
  };
}

function shotMs(overlay: ShotNumberOverlay): {
  startMs: number | null;
  endMs: number | null;
} {
  const raw = overlay.rawShot as any;
  const startMs = num(
    overlay.trajectoryStart?.ms ??
      raw?.start_ms ??
      raw?.resulting_ball_movement?.trajectory?.start?.ms,
  );
  const endMs = num(
    overlay.trajectoryEnd?.ms ??
      raw?.end_ms ??
      raw?.resulting_ball_movement?.trajectory?.end?.ms,
  );
  return { startMs, endMs };
}

export function computeAtpEventsForRally(args: {
  rally: KitchenArrivalRow;
  rallyIndex: number;
  playerIdToTeam?: (playerId: number | null) => 0 | 1 | null;
}): AtpEvent[] {
  const overlays = [...(args.rally.shotNumberOverlays ?? [])].sort(
    (a, b) => (a.shotNumber ?? 0) - (b.shotNumber ?? 0),
  );
  const out: AtpEvent[] = [];

  for (let i = 0; i < overlays.length; i += 1) {
    const overlay = overlays[i];
    const playerId = num(overlay.playerId ?? overlay.rawShot?.player_id);
    if (playerId == null) continue;

    const methods: AtpDetectionMethod[] = [];
    const notes: string[] = [];

    const isExplicitAtp = explicitAtpShotType(overlay);
    if (hasPbVisionAtpFlag(overlay)) {
      methods.push("pbVisionFlag");
      notes.push("PB Vision marked this shot as ATP");
    }

    const path = detectAtpByBallPath(overlay);
    if (!isExplicitAtp && path.invalidAboveNetCrossing) {
      continue;
    }
    if (path.detected) {
      methods.push("ballPath");
      notes.push(...path.notes);
    }

    if (!methods.length) continue;

    const { startMs, endMs } = shotMs(overlay);
    if (startMs == null && endMs == null) continue;

    const fallbackStart =
      num(overlay.rawShot?.start_ms) ?? overlay.atMsRel ?? 0;
    const fallbackEnd = num(overlay.rawShot?.end_ms) ?? fallbackStart + 700;

    out.push({
      rallyIndex: args.rallyIndex,
      shotIndex: i,
      shotNumber: overlay.shotNumber ?? i + 1,
      team: args.playerIdToTeam?.(playerId) ?? teamFromPlayerId(playerId),
      playerId,
      methods: Array.from(new Set(methods)),
      notes: Array.from(new Set(notes)),
      shotStartMs: startMs ?? fallbackStart,
      shotEndMs: endMs ?? fallbackEnd,
      shotType: overlay.shotType ?? overlay.rawShot?.shot_type ?? null,
      strokeType: (overlay.rawShot as any)?.stroke_type ?? null,
      startX: path.startX,
      endX: path.endX,
      peakZ: path.peakZ,
      netCrossingX: path.netCrossingX,
      netCrossingZ: path.netCrossingZ,
      heightOverNet: path.heightOverNet,
      netClearanceAtCrossing: path.netClearanceAtCrossing,
      outsideByFt: path.outsideByFt,
    });
  }

  return out;
}

export function buildAtpClip(args: {
  event: AtpEvent;
  overlays: ShotNumberOverlay[];
  rallyStartMs?: number;
  rallyEndMs?: number;
}): AtpClip {
  const overlays = [...(args.overlays ?? [])].sort(
    (a, b) => (a.shotNumber ?? 0) - (b.shotNumber ?? 0),
  );
  const shotNumber = args.event.shotNumber;
  const prev = overlays.find((o) => o.shotNumber === shotNumber - 1) ?? null;
  const next = overlays.find((o) => o.shotNumber === shotNumber + 1) ?? null;

  const prevMs = prev ? shotMs(prev) : null;
  const nextMs = next ? shotMs(next) : null;

  let clipStartMs =
    (prevMs?.startMs ?? args.event.shotStartMs - 900) -
    ATP_CLIP_EXTRA_BEFORE_PREV_SHOT_MS;
  let clipEndMs =
    (nextMs?.endMs ?? args.event.shotEndMs + 900) +
    (nextMs?.endMs != null ? 250 : 0);

  if (typeof args.rallyStartMs === "number") {
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  }
  if (typeof args.rallyEndMs === "number") {
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);
  }
  if (clipEndMs - clipStartMs < 1200) clipEndMs = clipStartMs + 1200;

  return {
    key: `r${args.event.rallyIndex}_s${args.event.shotNumber}_atp_${args.event.methods.join("-")}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event: args.event,
    clipStartMs,
    clipEndMs,
  };
}

export function computeAtpClipsFromRows(
  rows: KitchenArrivalRow[],
  playerIdToTeam?: (playerId: number | null) => 0 | 1 | null,
): AtpClip[] {
  const clips: AtpClip[] = [];
  for (const row of rows ?? []) {
    const rallyIndex = row.rallyIndex ?? 0;
    const overlays = row.shotNumberOverlays ?? [];
    const events = computeAtpEventsForRally({ rally: row, rallyIndex, playerIdToTeam });
    for (const event of events) {
      clips.push(
        buildAtpClip({
          event,
          overlays,
          rallyStartMs: row.segmentStartMs,
          rallyEndMs: row.segmentEndMs,
        }),
      );
    }
  }
  return clips;
}

function methodLabel(method: AtpDetectionMethod): string {
  return method === "pbVisionFlag" ? "PB Vision" : "Ball path";
}

function AtpBadge(props: { methods: AtpDetectionMethod[] }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.55,
        px: 0.85,
        py: 0.25,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 900,
        bgcolor: "rgba(25,118,210,0.12)",
        color: "rgba(12,73,130,0.95)",
        border: "1px solid rgba(25,118,210,0.26)",
        whiteSpace: "nowrap",
      }}
    >
      <SportsTennisIcon sx={{ fontSize: 13 }} />
      ATP
      <Box component="span" sx={{ opacity: 0.72, fontWeight: 800 }}>
        {props.methods.map(methodLabel).join(" + ")}
      </Box>
    </Box>
  );
}

export function ATPPopup(props: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  packageId: string;
  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];
  clipsThisRally: AtpClip[];
  clipsAll: AtpClip[];
  playerLabel?: (playerId: number) => string;
  renderPlayerIdentity: RenderPlayerIdentity;
  teamLabel?: (team: 0 | 1) => string;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    onClose,
    packageId,
    activeRallyIndex,
    rows,
    clipsThisRally,
    clipsAll,
    playerLabel,
    renderPlayerIdentity,
    teamLabel,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
  } = props;

  const [pos, setPos] = React.useState({ x: 24, y: 72 });
  const [scope, setScope] = React.useState<ScopeMode>("allRallies");
  const [methodFilter, setMethodFilter] = React.useState<MethodFilter>("all");
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 260, d.startPosX + e.clientX - d.startX)),
        y: Math.max(0, Math.min(window.innerHeight - 160, d.startPosY + e.clientY - d.startY)),
      });
    };
    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const baseClips = scope === "thisRally" ? clipsThisRally : clipsAll;
  const filteredClips = React.useMemo(() => {
    return baseClips.filter((clip) => {
      if (methodFilter === "all") return true;
      return clip.event.methods.includes(methodFilter);
    });
  }, [baseClips, methodFilter]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedKey(filteredClips[0]?.key ?? null);
  }, [open, activeRallyIndex, scope, methodFilter, filteredClips]);

  const selected =
    filteredClips.find((clip) => clip.key === selectedKey) ?? filteredClips[0] ?? null;

  const selectedRow = React.useMemo(() => {
    if (!selected) return null;
    return rows.find((row) => row.rallyIndex === selected.event.rallyIndex) ?? null;
  }, [rows, selected]);

  const clipRow = React.useMemo(() => {
    if (!selected || !selectedRow) return null;
    return selectedRow;
  }, [selected, selectedRow]);

  const selectedLoopSeed = React.useMemo(() => {
    if (!selected || !selectedRow) return null;
    const rallyStartMs =
      typeof selectedRow.segmentStartMs === "number"
        ? selectedRow.segmentStartMs
        : selected.clipStartMs;
    const rallyEndMs =
      typeof selectedRow.segmentEndMs === "number"
        ? selectedRow.segmentEndMs
        : selected.clipEndMs;
    const aSec = Math.max(0, (selected.clipStartMs - rallyStartMs) / 1000);
    const bSec = Math.max(aSec + 0.5, (selected.clipEndMs - rallyStartMs) / 1000);
    const maxSec = Math.max(bSec, (rallyEndMs - rallyStartMs) / 1000);
    return {
      playbackTimeSec: Math.min(aSec, Math.max(0, maxSec - 0.05)),
      loop: {
        enabled: true,
        aSec,
        bSec: Math.min(bSec, maxSec),
      },
    };
  }, [selected, selectedRow]);

  const [seedNonce, setSeedNonce] = React.useState(0);
  React.useEffect(() => {
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [selected?.key]);

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1180,
        maxWidth: "calc(100vw - 36px)",
        height: "88vh",
        maxHeight: "calc(100vh - 96px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",
      }}
    >
      <Box
        onMouseDown={onMouseDownHeader}
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SportsTennisIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.45)" }} />
          <Box sx={{ fontWeight: 900, fontSize: 18 }}>ATP Candidates</Box>
          <Box sx={{ fontSize: 12, opacity: 0.7 }}>
            {filteredClips.length} event{filteredClips.length === 1 ? "" : "s"}
          </Box>
        </Box>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: 36, width: 36, height: 36, borderRadius: 999 }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          gap: 1,
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          flexWrap: "wrap",
        }}
      >
        <ToggleButtonGroup exclusive size="small" value={scope} onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value="allRallies">All rallies</ToggleButton>
          <ToggleButton value="thisRally">This rally</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup exclusive size="small" value={methodFilter} onChange={(_, v) => v && setMethodFilter(v)}>
          <ToggleButton value="all">All methods</ToggleButton>
          <ToggleButton value="pbVisionFlag">PB Vision flagged</ToggleButton>
          <ToggleButton value="ballPath">Ball path</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box sx={{ width: 430, minWidth: 430, borderRight: "1px solid rgba(0,0,0,0.10)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Method</TableCell>
                  <TableCell>Rally</TableCell>
                  <TableCell>Shot</TableCell>
                  <TableCell>Player</TableCell>
                  <TableCell align="right">Outside</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClips.map((clip) => {
                  const ev = clip.event;
                  const isSel = clip.key === selected?.key;
                  return (
                    <TableRow
                      key={clip.key}
                      hover
                      selected={isSel}
                      onClick={() => setSelectedKey(clip.key)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <AtpBadge methods={ev.methods} />
                      </TableCell>
                      <TableCell>{ev.rallyIndex + 1}</TableCell>
                      <TableCell>{ev.shotNumber}</TableCell>
                      <TableCell>
                        {renderPlayerIdentity
                          ? renderPlayerIdentity({
                              playerId: ev.playerId,
                              labelOnly: false,
                              size: 26,
                              borderRadius: 5,
                              fontSize: 12,
                            })
                          : playerLabel?.(ev.playerId) ?? `p${ev.playerId}`}
                        <Box sx={{ fontSize: 11, opacity: 0.65 }}>
                          {teamLabel ? teamLabel(ev.team) : `Team ${ev.team}`}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {ev.outsideByFt != null ? ev.outsideByFt.toFixed(1) : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {selected ? (
            <Box sx={{ p: 1.2, borderTop: "1px solid rgba(0,0,0,0.10)", bgcolor: "rgba(0,0,0,0.02)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                <AtpBadge methods={selected.event.methods} />
                <Box sx={{ fontWeight: 900 }}>
                  Rally {selected.event.rallyIndex + 1} - Shot {selected.event.shotNumber}
                </Box>
              </Box>
              <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                <Box>Player: {playerLabel?.(selected.event.playerId) ?? `p${selected.event.playerId}`}</Box>
                <Box>Shot type: {selected.event.shotType ?? "-"}</Box>
                <Box>
                  Ball x: {selected.event.startX?.toFixed(1) ?? "-"} to {selected.event.endX?.toFixed(1) ?? "-"}
                  {selected.event.peakZ != null ? `, peak z ${selected.event.peakZ.toFixed(1)}` : ""}
                  {selected.event.netCrossingX != null && selected.event.netCrossingZ != null
                    ? `, net x ${selected.event.netCrossingX.toFixed(1)} z ${selected.event.netCrossingZ.toFixed(1)}`
                    : ""}
                  {selected.event.heightOverNet != null
                    ? `, PBV net clearance ${selected.event.heightOverNet.toFixed(1)}`
                    : selected.event.netClearanceAtCrossing != null
                      ? `, estimated net clearance ${selected.event.netClearanceAtCrossing.toFixed(1)}`
                    : ""}
                </Box>
                <Box>
                  Loop: full rally video, scrubber set before the prior shot through the following shot when available.
                </Box>
              </Box>
              <Box sx={{ mt: 0.9, display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                {selected.event.notes.map((note, i) => (
                  <Box
                    key={`${selected.key}_${i}`}
                    sx={{
                      px: 0.8,
                      py: 0.2,
                      borderRadius: 999,
                      fontSize: 11,
                      bgcolor: "rgba(0,0,0,0.05)",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    {note}
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, bgcolor: "black", display: "flex", flexDirection: "column" }}>
          {clipRow && selected && selectedLoopSeed ? (
            <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
              <ActiveRallyVideo
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                renderPlayerIdentity={renderPlayerIdentity}
                aiContextLabel="ATP Candidates"
                focusedFilter={null}
                packageId={packageId}
                row={clipRow}
                rows={rows}
                activeShotNumber={selected.event.shotNumber}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={true}
                aiDrawerMode="split"
                showLoopScrubber={true}
                defaultLoopUiOpen={true}
                showPopoutButton={true}
                isInFullPopup={false}
                seekToActiveShotOnChange={false}
                seedPlayback={{
                  timeSec: selectedLoopSeed.playbackTimeSec,
                  paused: false,
                  nonce: seedNonce,
                }}
                seedLoopRegion={{
                  ...selectedLoopSeed.loop,
                  nonce: seedNonce,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  px: 1,
                  py: 0.6,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.66)",
                  color: "white",
                  fontSize: 12,
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AtpBadge methods={selected.event.methods} />
                <Box sx={{ fontWeight: 900 }}>
                  Rally {selected.event.rallyIndex + 1} - Shot {selected.event.shotNumber}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", opacity: 0.72 }}>
              No ATP candidate selected
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
